import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import { DialogShellComponent } from 'src/app/core/ui/dialog-shell/dialog-shell.component';
import {
  ErrorNotificationBlock,
  ErrorNotificationContent,
  ErrorNotificationLink,
  errorNotificationBlocks,
} from 'src/app/core/services/error-notification-rules';

/**
 * Error notification, shown as a dialog the user closes with OK (project rule: an error is never a
 * toast that disappears before it is read). Built on the shared dialog shell, tone `danger`, with
 * no close icon: OK is the only way out. Text only — the message is interpolated, never bound as
 * HTML, because it often carries values from the database or from an API response. A message that
 * lists what to do (one bullet line per action, as the notice endpoints write it) is rendered as a
 * list; the service widens the dialog for it. Links (where the action is done) sit next to OK.
 */
@Component({
  selector: 'app-error-notification-dialog',
  standalone: true,
  imports: [MatDialogClose, MatButtonModule, TablerIconsModule, DialogShellComponent],
  template: `
    <app-dialog-shell [title]="data.title" tone="danger" icon="alert-circle">
      @for (block of blocks; track $index) {
        @if (block.type === 'list') {
          <ul class="error-actions">
            @for (line of block.lines; track $index) {
              <li class="error-message">{{ line }}</li>
            }
          </ul>
        } @else {
          @for (line of block.lines; track $index) {
            <p class="error-message">{{ line }}</p>
          }
        }
      }

      <ng-container dialogActions>
        @for (link of links; track link.id) {
          <button mat-flat-button type="button" class="ap-dialog-btn ap-dialog-btn--light error-link-btn" (click)="follow(link)">
            <i-tabler name="external-link" class="icon-16"></i-tabler>
            {{ link.label }}
          </button>
        }
        <button mat-flat-button type="button" class="ap-dialog-btn ap-dialog-btn--primary error-ok-btn" [mat-dialog-close]="true">
          OK
        </button>
      </ng-container>
    </app-dialog-shell>
  `,
  styles: [`
    .error-message {
      margin: 0 0 8px;
      line-height: 1.5;
      overflow-wrap: anywhere;
      user-select: text;
    }

    .error-message:last-child {
      margin-bottom: 0;
    }

    .error-actions {
      margin: 4px 0 8px;
      padding-left: 22px;
    }

    .error-actions li {
      margin-bottom: 6px;
    }

    .error-link-btn {
      text-align: left;
    }
  `],
})
export class ErrorNotificationDialogComponent {
  readonly blocks: ErrorNotificationBlock[];
  readonly links: ErrorNotificationLink[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ErrorNotificationContent,
    private dialogRef: MatDialogRef<ErrorNotificationDialogComponent, string | boolean>,
  ) {
    this.blocks = errorNotificationBlocks(data.message);
    this.links = data.links ?? [];
  }

  /** Closes with the link id: the caller navigates (the dialog knows no route). */
  follow(link: ErrorNotificationLink): void {
    this.dialogRef.close(link.id);
  }
}
